import { create } from 'zustand';
import type { SignUpSource } from '@/analytics';
import { resolveSignupGate, waitForSignupGate, type SignupGateOutcome } from '@/lib/auth/signup-gate';

type AuthModalMode = 'login' | 'signup' | 'verifyEmail' | 'forgotPassword' | null;

interface AuthModalState {
  modal: AuthModalMode;
  /** Entry point of the current signup funnel — read by `sign_up_completed`. */
  signupSource: SignUpSource | null;
  /** Email to pre-fill in the signup form — set by hand-offs that already know
   *  it (the login form's "no account found → Sign up" link). Cleared on close
   *  and on opens that don't carry one, so it never leaks into a fresh funnel. */
  signupPrefillEmail: string | null;
  openLogin: () => void;
  /** Open the signup sheet. `source` records the entry point for analytics;
   *  internal re-opens (resume, login→signup hand-off) omit it to preserve the
   *  source captured at the original `sign_up_started`. */
  openSignup: (source?: SignUpSource, opts?: { prefillEmail?: string }) => void;
  /** Show the email-verification step (rendered by the app-level
   *  EmailVerificationPage, which keys off this mode). Modal-driven so a page
   *  refresh — which resets the store — leaves the funnel closed until the user
   *  re-enters it, instead of the verification sheet forcing itself open. */
  openVerifyEmail: () => void;
  /** Open the forgot-password sheet (email entry → branded reset email). */
  openForgotPassword: () => void;
  close: () => void;
}

const useAuthModalStore = create<AuthModalState>((set) => ({
  modal: null,
  signupSource: null,
  signupPrefillEmail: null,
  openLogin: () => set({ modal: 'login' }),
  openSignup: (source, opts) =>
    set((s) => ({
      modal: 'signup',
      signupSource: source ?? s.signupSource,
      signupPrefillEmail: opts?.prefillEmail ?? null,
    })),
  openVerifyEmail: () => set({ modal: 'verifyEmail' }),
  openForgotPassword: () => set({ modal: 'forgotPassword' }),
  close: () => {
    // Closing without completing abandons any awaited signup gate. On genuine
    // completion the funnel resolves 'completed' before closing, so this is a
    // no-op there (empty resolver list).
    resolveSignupGate('dismissed');
    set({ modal: null, signupPrefillEmail: null });
  },
}));

/**
 * Non-hook signup-gate opener for the 403 SIGNUP_INCOMPLETE interceptor (mirrors
 * `openKycGateImperative`). Opens the signup funnel — the sheet itself routes a
 * mid-signup account to its next step (verify email / terms) — and resolves
 * 'completed' when the funnel finishes, 'dismissed' when it's closed or
 * abandoned (logout). No analytics source: an interceptor doesn't know the
 * product entry point, and omitting preserves any source already captured.
 */
export const openSignupGateImperative = (): Promise<SignupGateOutcome> => {
  useAuthModalStore.getState().openSignup();
  return waitForSignupGate();
};

/** Select the current modal mode ('login' | 'signup' | 'forgotPassword' | null). */
export const useAuthModal = () => useAuthModalStore((s) => s.modal);

/** Select the openLogin action. */
export const useOpenLogin = () => useAuthModalStore((s) => s.openLogin);

/** Select the openForgotPassword action. */
export const useOpenForgotPassword = () => useAuthModalStore((s) => s.openForgotPassword);

/** Select the openSignup action. */
export const useOpenSignup = () => useAuthModalStore((s) => s.openSignup);

/** Select the openVerifyEmail action. */
export const useOpenVerifyEmail = () => useAuthModalStore((s) => s.openVerifyEmail);

/** Select the close action. */
export const useCloseAuthModal = () => useAuthModalStore((s) => s.close);

/** Select the entry-point source captured when the signup funnel opened. */
export const useSignupSource = (): SignUpSource | null => useAuthModalStore((s) => s.signupSource);

/** Email a hand-off asked the signup form to start with (or null). */
export const useSignupPrefillEmail = (): string | null => useAuthModalStore((s) => s.signupPrefillEmail);
